import React, { createContext, useContext, useRef, useState, useLayoutEffect, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";

const DragCtx = createContext(null);

const loadPos = (key) => {
  try { const v = JSON.parse(localStorage.getItem("dragpos:" + key)); if (v && typeof v.x === "number" && typeof v.y === "number") return v; } catch { /* ignore */ }
  return null;
};
const savePos = (key, pos) => { try { localStorage.setItem("dragpos:" + key, JSON.stringify(pos)); } catch { /* ignore */ } };

// A floating panel you can drag anywhere. No backdrop — everything beneath stays interactive.
// Position is remembered per `storageKey` in localStorage.
export function useDraggable(storageKey) {
  const ref = useRef(null);
  const [pos, setPos] = useState(() => loadPos(storageKey));

  const clampPos = useCallback((p) => {
    const el = ref.current;
    const w = el?.offsetWidth || 320;
    const pad = 60;
    const x = Math.min(Math.max(p.x, pad - w), window.innerWidth - pad);
    const y = Math.min(Math.max(p.y, 0), window.innerHeight - 40);
    return { x, y };
  }, []);

  // Center on first mount when there is no remembered position.
  useLayoutEffect(() => {
    if (pos || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos({ x: Math.max(8, (window.innerWidth - r.width) / 2), y: Math.max(8, (window.innerHeight - r.height) / 2) });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep it reachable if the window is resized.
  useEffect(() => {
    const onResize = () => setPos((p) => (p ? clampPos(p) : p));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampPos]);

  const startDrag = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest("button, input, select, textarea, a, [data-no-drag]")) return;
    const el = ref.current; if (!el) return;
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const base = pos || { x: rect.left, y: rect.top };
    const start = { mx: e.clientX, my: e.clientY };
    const move = (ev) => setPos(clampPos({ x: base.x + (ev.clientX - start.mx), y: base.y + (ev.clientY - start.my) }));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setPos((p) => { if (p) savePos(storageKey, p); return p; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }, [pos, storageKey, clampPos]);

  const style = pos
    ? { position: "fixed", left: pos.x, top: pos.y }
    : { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" };

  return { ref, style, startDrag };
}

export function DraggablePanel({ storageKey, className, style: extraStyle, children, ...rest }) {
  const { ref, style, startDrag } = useDraggable(storageKey);
  return createPortal(
    <div ref={ref} style={{ ...style, ...extraStyle }} className={cn("z-[70] pointer-events-auto", className)} {...rest}>
      <DragCtx.Provider value={startDrag}>{children}</DragCtx.Provider>
    </div>,
    document.getElementById("takeoff-fs-root") || document.body
  );
}

// Wrap any header/title bar with this to make it the drag grip.
export function DragHandle({ className, children, ...p }) {
  const startDrag = useContext(DragCtx);
  return (
    <div {...p} onPointerDown={startDrag} className={cn("cursor-move select-none touch-none", className)}>
      {children}
    </div>
  );
}
